// Vendor
import { useRouter } from 'vue-router';

// PERSONAL LIBS
import { useUiStore } from 'src/stores/ui';
import { DeviceOrientationEnum } from '../enum/device-orientation.enum';
import { ReloadMethodEnum } from '../enum/reload-method.enum';
import { CronJob } from '@shared/service/cronjob/cronjob.service';
import bus from '@shared/lib/eventbus.lib';
import { BusEventDialogEnum } from '@shared/enum/bus/bus-events-dialog.enum';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { logger } from './logger/logger-manager';
import { DexieErrorEnum } from '@shared/enum/dexie/error.enum';
import { fullReload } from './browser.lib';
import { BusEventEnum } from '@shared/enum/bus/bus-events.enum';
import { RouteEnum } from '@shared/enum/route.enum';

/**
 * Method called in 'unhandledrejection' window listener
 * @param error
 */
export const unhandledrejectionHandler = (error: PromiseRejectionEvent) => {
  let errorType = '';
  if (Object.values(DexieErrorEnum).includes(error.reason.name as DexieErrorEnum)) { // Dexie Errors
    errorType = 'Dexie Error'

    if (DexieErrorEnum.DATABASE_CLOSED_ERROR === (error.reason.name as DexieErrorEnum)) {
      fullReload(ReloadMethodEnum.METHOD_1)
    }
  } else { // Generic Errors
    errorType = 'Generic Error'
  }

  logger?.log(`[unhandledrejectionHandler] - ${errorType} - error.reason.name : ${String(error.reason.name)}`)
  logger?.log(`[unhandledrejectionHandler] - ${errorType} - error.reason.message : ${String(error.reason.message)}`)
}

/**
 * Method called in 'orientationchange' window listener
 */
export const orientationStrategyHandler = () => {
  const isLandscape = window.orientation === 90 || window.orientation === -90;
  const isPortrait = !isLandscape;
  const uiStore = useUiStore();
  uiStore.uiOrientation = isPortrait
    ? DeviceOrientationEnum.PORTRAIT
    : DeviceOrientationEnum.LANDSCAPE;
};

/**
 * Method called in 'beforeunload' window listener
 * @param e BeforeUnloadEvent
 */
export const beforeUnloadHandler = () => {
  // Stop all tasks
  new CronJob().stopAll();
};

/**
 * Method called in 'onpopstate' window listener
 * @param e UnloadEvent
 */
export const popStateHandler = () => {
  window.history.pushState(null, '', window.location.href);
  bus.emit(BusEventDialogEnum.CLOSE_LAST_MODAL);
};

/**
 * Method called in 'online' window listener
 */
export const setOnline = () => {
  sessionManager.isOnline.value = true
};

/**
 * Method called in 'offline' window listener
 */
export const setOffline = () => {
  sessionManager.isOnline.value = false
};

/**
 * Add event listeners
 */
export const addEventListeners = () => {
  if (window.addEventListener) {
    // Catch error event
    window.addEventListener('unhandledrejection', unhandledrejectionHandler)
    window.onunhandledrejection = unhandledrejectionHandler

    // Listen for orientation changes
    window.addEventListener(
      'orientationchange',
      orientationStrategyHandler,
      false,
    );

    // Application state management: listen event before exit
    window.addEventListener('beforeunload', beforeUnloadHandler, false);

    // Bloc the back button
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', popStateHandler, false);
  }
};

/**
 * Add bus listeners
 */
export const addBusListeners = () => {
  const router = useRouter();
  
  bus.on(BusEventEnum.GO_TO_HOME, () => {
    router.push(`/${RouteEnum.HOME}`);
  });
};

/**
 * Removes all event listeners
 */
export const removeEventListeners = () => {
  if (window.removeEventListener) {
    window.removeEventListener('unhandledrejection', unhandledrejectionHandler, false);
    window.removeEventListener(
      'orientationchange',
      orientationStrategyHandler,
      false,
    );
    window.removeEventListener('beforeunload', beforeUnloadHandler, false);
    window.removeEventListener('popstate', popStateHandler, false);
  }
};

/**
 * Removes all bus listeners
 */
export const removeBusListeners = () => {
  // Bus off
  bus.off(BusEventDialogEnum.CLOSE_LAST_MODAL);
  bus.off(BusEventDialogEnum.CLOSE_ALL_MODAL);
  bus.off(BusEventEnum.GO_TO_HOME);
};